import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import StepHeader from '@components/quiz/shared/StepHeader';
import CtaButton from '@components/quiz/shared/CtaButton';
import TrustpilotRating from '@components/quiz/shared/TrustpilotRating';
import AsSeenOnStrip from '@components/quiz/shared/AsSeenOnStrip';
import type { SanityGytrEmailGateBlock } from '@lib/sanity/types';

type GytrEmailGateStepProps = SanityGytrEmailGateBlock & {
  loginUrl: string;
  onSubmit: (email: string) => void;
  isSubmitting: boolean;
};

const emailSchema = z.object({ email: z.string().email() });
type EmailFormValues = z.infer<typeof emailSchema>;

/**
 * Step 2: the hard email gate (spec 7). Carries the heaviest social proof
 * in the flow — Trustpilot rating plus the as-seen-on strip — sized below
 * the headline. The rating is the reward delivered on the next screen.
 */
const GytrEmailGateStep: FC<GytrEmailGateStepProps> = ({
  header,
  trustpilotTemplateId,
  trustpilotHeight,
  trustpilotWidth,
  asSeenOnLabel,
  asSeenOnItems,
  asSeenOnMonochrome,
  asSeenOnLogoHeight,
  emailPlaceholder,
  consentText,
  invalidEmailText,
  ctaText,
  loginUrl,
  onSubmit,
  isSubmitting
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange'
  });

  const submit = handleSubmit((data) => {
    if (isSubmitting) return;
    onSubmit(data.email);
  });

  return (
    <section className="flex flex-col items-center px-6 pb-12 pt-6">
      <div className="w-full max-w-sm">
        <StepHeader
          eyebrow={header?.eyebrow}
          accentHeading={header?.heading}
          subtext={header?.subtext}
        />

        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white px-5 py-4">
          <TrustpilotRating
            templateId={trustpilotTemplateId}
            height={trustpilotHeight}
            width={trustpilotWidth}
          />
          {asSeenOnItems && asSeenOnItems.length > 0 && (
            <>
              <div className="my-4 border-t border-dashed border-neutral-200" />
              <AsSeenOnStrip
                items={asSeenOnItems}
                label={asSeenOnLabel}
                monochrome={asSeenOnMonochrome}
                logoHeight={asSeenOnLogoHeight}
              />
            </>
          )}
        </div>

        <form onSubmit={submit} className="mt-6" noValidate>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={emailPlaceholder ?? 'Your email address'}
            data-cy="gytr-email-input"
            className={`w-full rounded-2xl border bg-white px-[18px] py-4 font-body text-base text-neutral-800 outline-none transition-all focus:border-primary-600 ${
              errors.email ? 'border-danger-400' : 'border-neutral-300'
            }`}
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-2 font-body text-sm text-danger-600">
              {invalidEmailText ?? 'Please enter a valid email address.'}
            </p>
          )}
          {consentText && (
            <p className="mt-4 text-center font-body text-xs text-neutral-500">
              {consentText}
            </p>
          )}
          <div className="mt-5">
            <CtaButton
              type="submit"
              disabled={!isValid || isSubmitting}
              dataCy="gytr-email-cta"
            >
              {isSubmitting ? 'One moment…' : ctaText ?? 'See my rating'}
            </CtaButton>
          </div>
        </form>

        {/* Escape hatch for existing members bounced here from a protected
            page (e.g. an onboarding email CTA) while logged out. */}
        <a
          href={loginUrl}
          data-cy="gytr-email-login-link"
          className="mt-3 block w-full text-center font-body text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
        >
          Already a member? Log in
        </a>
      </div>
    </section>
  );
};

export default GytrEmailGateStep;
